import React from 'react';
import {Icon, Tooltip} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {FlightLeg} from '../../../schema';
import {POSITIONING_LEG_TOOLTIP} from '../../flighteditor/unified/positioning-leg-tag';
import './flight-leg-load-columns.less';

export function isPositioningLeg(leg: FlightLeg): boolean {
    return !(leg.paxCount || 0) && !(leg.cgoCount || 0);
}

/**
 * Compact per-leg loads shared by automatic and manual assignment in both editors.
 * Passenger weight excludes bags; cargo load includes cargo and all passenger bags.
 * Counts remain visible below weights, with combined weight in its own final column.
 */
export function createFlightLegLoadColumns(
    renderStatus?: (leg: FlightLeg) => React.ReactNode,
    getSelectedCounts?: (leg: FlightLeg) => {pax: number; cgo: number}
): ColumnProps<FlightLeg>[] {
    function routeDetail(leg: FlightLeg) {
        const selected = getSelectedCounts?.(leg) ?? {pax: 0, cgo: 0};
        const count = selected.pax + selected.cgo;
        const total = Math.max(leg.paxCount || 0, (leg.paxIDs || []).length) +
            Math.max(leg.cgoCount || 0, (leg.cgoIDs || []).length);
        const label = isPositioningLeg(leg) ? 'Positioning' : 'Loaded';
        const statusTooltip = isPositioningLeg(leg)
            ? POSITIONING_LEG_TOOLTIP
            : 'This leg carries passengers and/or cargo.';
        return (
            <span className="flight-leg-load__selection-slot">
                <span
                    className="flight-leg-load__selection-reserve"
                    aria-hidden="true"
                    data-selection-placeholder={`${total} selected`}
                />
                <span className="flight-leg-load__selection-reserve" aria-hidden="true" data-selection-placeholder={label}/>
                <Tooltip title={count > 0 ? `${selected.pax} PAX and ${selected.cgo} CGO selected` : statusTooltip}>
                    <span className={count > 0 ? 'flight-leg-load__selected' : 'flight-leg-load__status'}>
                        {count > 0 ? `${count} selected` : label}
                    </span>
                </Tooltip>
            </span>
        );
    }

    return [
        {
            title: 'Leg',
            key: 'route',
            className: 'flight-leg-load__route-cell',
            render: (_, leg, index) => {
                const positioning = isPositioningLeg(leg);
                return (
                    <div className="flight-leg-load__itinerary">
                        <Tooltip title={positioning ? 'Empty positioning leg' : `Leg ${index + 1}`}>
                            <span
                                className={`flight-leg-load__number${positioning ? ' flight-leg-load__number--positioning' : ''}`}
                                aria-label={`Leg ${index + 1}${positioning ? ', empty positioning leg' : ''}`}
                            >
                                {index + 1}
                            </span>
                        </Tooltip>
                        <div className="flight-leg-load__route-content">
                            <div className="flight-leg-load__route">
                                <span>{leg.departure || leg.departureID}</span>
                                <Icon type="arrow-right" aria-label="to"/>
                                <span>{leg.destination || leg.destinationID}</span>
                            </div>
                            <div className="flight-leg-load__detail">
                                {routeDetail(leg)}
                                {renderStatus?.(leg)}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'PAX',
            key: 'paxLoad',
            render: (_, leg) => (
                <div>
                    <Tooltip title={`Weight: ${leg.paxWeight ?? 0}`}>
                        <strong className="flight-leg-load__weight" aria-label={`Passenger weight ${leg.paxWeight ?? 0} WT`}>
                            {leg.paxWeight ?? 0} WT
                        </strong>
                    </Tooltip>
                    <div className="flight-leg-load__detail">
                        <span className="flight-leg-load__count">{leg.paxCount ?? 0} pax</span>
                    </div>
                </div>
            )
        },
        {
            title: 'CGO + bags',
            key: 'cargoLoad',
            render: (_, leg) => {
                const weight = Number(leg.cgoWeight ?? 0) + Number(leg.bagWeight ?? 0);
                return (
                    <div>
                        <Tooltip title={`Weight: ${weight}`}>
                            <strong className="flight-leg-load__weight" aria-label={`Cargo and bags weight ${weight} WT`}>
                                {weight} WT
                            </strong>
                        </Tooltip>
                        <div className="flight-leg-load__detail">
                            {leg.cgoCount ?? 0} cgo · {leg.bagCount ?? 0} bags
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Total',
            key: 'totalLoad',
            render: (_, leg) => {
                const weight = Number(leg.paxWeight ?? 0) + Number(leg.cgoWeight ?? 0) + Number(leg.bagWeight ?? 0);
                return (
                    <Tooltip title={`Weight: ${weight}`}>
                        <strong className="flight-leg-load__weight" aria-label={`Total weight ${weight} WT`}>
                            {weight} WT
                        </strong>
                    </Tooltip>
                );
            }
        }
    ];
}
